/**
 * Carbon Offset utilities.
 *
 * Optional carbon offset contribution at checkout.
 * Carbon offset amount is calculated based on booking value
 * or a fixed amount per booking.
 *
 * Config from CARBON_OFFSET_JSON env var:
 * {
 *   "ratePct": 1,              — 1% of booking value
 *   "fixedCents": null,        — OR fixed amount (e.g. 100 = €1)
 *   "currency": "EUR",
 *   "provider": "Atmosfair",   — offset provider name
 *   "description": "Plant trees with every booking"
 * }
 *
 * Selected: transaction.protectedData.carbonOffset = { contributed: true, amountCents }
 */

export const DEFAULT_CARBON_RATE_PCT = 1;

/**
 * Calculate carbon offset amount.
 * @param {number} bookingAmountCents
 * @param {Object} config — { ratePct, fixedCents }
 * @returns {number}
 */
export const calculateOffset = (bookingAmountCents, config = {}) => {
  const { ratePct = DEFAULT_CARBON_RATE_PCT, fixedCents } = config;
  if (fixedCents) return fixedCents;
  return Math.max(1, Math.round(bookingAmountCents * (ratePct / 100)));
};

/**
 * Get carbon offset from transaction.
 * @param {Object|null} transaction
 * @returns {Object|null}
 */
export const getCarbonOffset = (transaction) =>
  transaction?.attributes?.protectedData?.carbonOffset || null;

export const hasContributed = (transaction) =>
  getCarbonOffset(transaction)?.contributed === true;

/**
 * Estimate equivalent CO2 in kg.
 * Rule of thumb: €1 ≈ 1 kg CO2 offset.
 * @param {number} amountCents
 * @returns {number}
 */
export const estimateCO2kg = (amountCents) =>
  Math.round(amountCents / 100);
